import { useSelector } from "react-redux";
import { Flex } from "rebass/styled-components";
import { format, parseISO, isPast } from "date-fns";
import React, { useRef, useEffect, useState } from "react";
import { CommentDetail } from "@styled-icons/boxicons-solid";

import {
  EntryTop,
  SlotEntry,
  EntryTime,
  EntryBottom,
  EntryChannels
} from "./styles";
import { theme } from "theme";
import Popper from "components/common/Popper";
import { formatForTimeline } from "utils/date";
import TaskOptions from "../common/TaskOptions";
import {
  RightClickPostContextMenu,
  PostContextMenuWithButton
} from "../common/postContextMenu/PostContextMenu";
import { useComposerActions } from "contextApi/composerContext";
import EntryStatus from "components/common/contentTypes/EntryStatus";
import { RawToolTip, ToolTip, PostCreateButton } from "../common/styles";
import { Avatar, ChannelAvatar, Service } from "components/common/styles";
import defaultAvatar from "assets/images/dashboard/avatar-empty-state.png";
import contentTypes from "components/common/contentTypes/data/content-types";
import ContentTypeBadge from "components/common/contentTypes/ContentTypeBadge";

function MonthTask({ day, entry, dashboardEntry }) {
  const { id: eventId, task, preview: isPreview, isPhantom } = entry;
  const ref = useRef(null);
  const commentRef = useRef(null);
  const createPostByTask = useComposerActions(
    actions => actions.createPostByTask
  );
  const [contextMenuPos, setContextMenuPos] = useState([]);
  const draggingItemId = useSelector(state => state.dragDrop.draggingItemId);
  const isDragging = draggingItemId === task.id;

  const [isVisited, setIsVisited] = useState(false);

  const formattedTaskDate = formatForTimeline(new Date(task.date), true);
  const displayTime = format(parseISO(task.date), "H:mm");

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const toggleConfirmationModal = () =>
    setShowConfirmationModal(!showConfirmationModal);

  const handleAddPost = () => {
    createPostByTask(task);
  };

  const isPastTask = isPast(new Date(task.date));

  const [showOptions, setShowOptions] = useState(false);
  const toggleShowOptions = () => setShowOptions(!showOptions);
  const [showTaskDescription, setShowTaskDescription] = useState(false);

  const closeMenu = () => {
    setContextMenuPos([]);
  };

  // Context menu handler on right click
  useEffect(() => {
    const element = ref.current;

    const handleContextMenu = e => {
      e.preventDefault();
      setContextMenuPos([e.clientX, e.clientY]);
    };

    element.addEventListener("contextmenu", handleContextMenu);

    return () => {
      element.removeEventListener("contextmenu", handleContextMenu);
    };
  }, []);

  // Remove context menu on scroll
  useEffect(() => {
    const handleScroll = () => {
      setContextMenuPos([]);
    };

    if (!!contextMenuPos.length) {
      document.addEventListener("scroll", handleScroll);
    }

    return () => {
      document.removeEventListener("contextmenu", handleScroll);
    };
  }, [contextMenuPos]);

  return (
    <div>
      <SlotEntry
        ref={ref}
        onClick={dashboardEntry ? handleAddPost : toggleShowOptions}
        saved={task.id}
        isFilled={task.posts?.length > 0}
        isPast={isPastTask}
        preview={isPreview}
        previewColor={
          isPreview ? contentTypes[task.contentTypeId]?.previewColor : null
        }
        isPhantom={isPhantom}
        isOtherMonth={day.isOtherMonth}
        color={contentTypes[task.contentTypeId].color}
        bg={contentTypes[task.contentTypeId].bgColor}
        hover={contentTypes[task.contentTypeId].overColor}
        isDragging={isDragging}
        isContextMenuOpen={contextMenuPos.length > 0}
      >
        <EntryTop>
          <EntryTime preview={isPreview}>
            {dashboardEntry ? (
              <span className="date">{formattedTaskDate}</span>
            ) : (
              <>
                <EntryStatus
                  status={isPreview ? "TASK_SCHEDULE_PREVIEW" : "TASK_SCHEDULE"}
                />
                {displayTime} {task.postId && <small>(Filled)</small>}
              </>
            )}
          </EntryTime>
          <EntryChannels>
            {task.channels.map(channel => {
              return (
                <ChannelAvatar $enabled={true} key={`${eventId}-${channel.id}`}>
                  <Avatar
                    src={channel.avatar ?? defaultAvatar}
                    width={20}
                    height={20}
                    $noShadow={true}
                    $isRounded={true}
                    onError={event => {
                      event.target.src = defaultAvatar;
                      event.onerror = null;
                    }}
                  />
                  <Service
                    key={channel.id}
                    title={channel.username}
                    $type={channel.service}
                    $size={10}
                  />
                </ChannelAvatar>
              );
            })}
          </EntryChannels>
        </EntryTop>
        <EntryBottom>
          <Flex alignItems={"center"}>
            {!isPastTask && !isPreview && (
              <>
                <PostCreateButton
                  data-tip
                  data-for={`${task.id}_POST`}
                  onMouseLeave={() => setIsVisited(true)}
                  className="icon-task-check action__create-post"
                  onClick={e => {
                    e.stopPropagation();
                    handleAddPost();
                  }}
                >
                  <span className="path1"></span>
                  <span className="path2"></span>
                </PostCreateButton>
                <ToolTip
                  id={`${task.id}_POST`}
                  disable={isVisited}
                  place="top"
                  className="tooltip"
                  variant="info"
                >
                  Click here to create and write your post
                </ToolTip>
              </>
            )}
            <ContentTypeBadge
              className="post__content-type"
              idea={task.postIdea ?? null}
              contentTypeId={task.contentTypeId}
              size="sm"
              variant={
                !task.id && !task.postId
                  ? "white"
                  : isPreview
                    ? "dark"
                    : "dark-inverse"
              }
              title={
                task.title
                  ? task.title
                  : task.postIdea
                    ? task.postIdea.title
                    : contentTypes[task.contentTypeId].title
              }
            />
          </Flex>
          {task.description && (
            <>
              <CommentDetail
                color={"#1C1E1F"}
                size={18}
                data-tip
                ref={commentRef}
                onMouseOver={() => setShowTaskDescription(true)}
                onMouseLeave={() => setShowTaskDescription(false)}
              />
              <Popper
                placement="top"
                offset={[0, 5]}
                arrowBg={theme.colors.text01}
                visible={showTaskDescription}
                exceptions={[commentRef.current]}
                referenceElement={commentRef.current}
                onClose={() => setShowTaskDescription(false)}
              >
                <RawToolTip>{task.description}</RawToolTip>
              </Popper>
            </>
          )}
        </EntryBottom>
        {!isPreview && (
          <>
            <RightClickPostContextMenu
              entry={entry}
              closeMenu={closeMenu}
              contextMenuPos={contextMenuPos}
              deleteEntry={toggleConfirmationModal}
            />
            <PostContextMenuWithButton
              entry={entry}
              contextWithPortal={true}
              deleteEntry={toggleConfirmationModal}
            />
          </>
        )}
      </SlotEntry>
      <Popper
        referenceElement={ref.current}
        visible={showOptions}
        exceptions={[ref.current]}
        onClose={() => setShowOptions(false)}
      >
        {!isPreview ? (
          <TaskOptions entry={entry} close={() => setShowOptions(false)} />
        ) : null}
      </Popper>
    </div>
  );
}

export default MonthTask;
